/**
 * The Add Collector wizard's chrome — everything on the page that is the same
 * whether the step has loaded, is loading, or blew up.
 *
 * **Eager**, and shared with the skeleton for the reason `log-source-table-frame`
 * and `panel-frame` are: the step behind it is `lazy()`, so a skeleton that drew
 * its own copy of this header would be a second place for the wizard's title,
 * its rail and the card's explanation to drift.
 */
import { SectionDescription, SectionTitle } from '@/features/app-shell/section'
import { cn } from '@/utils/ui/styles'

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

import {
	COLLECTOR_HOST_REQUIREMENTS,
	INSTALL_COMMAND_SHELL,
} from '../../install-command'
import { WizardStepRail } from '../wizard-step-rail'

import type { ReactNode } from 'react'

/**
 * What the flow costs, in full, before it starts.
 *
 * Both steps are named even though only the first is built (ticket 07): the rail
 * is the promise that installing the agent is not the end of the job — a
 * Collector with no Log Sources on it receives nothing.
 */
const WIZARD_STEPS = ['Set up collector', 'Add log sources'] as const

/** Which step the rail marks as current. One-based, as the rail numbers it. */
const ACTIVE_STEP = 1

type AddCollectorFrameProps = {
	/** The step's body, inside the card. */
	children: ReactNode
	/**
	 * The step's actions, **below** the card rather than in a `CardFooter` — they
	 * move the wizard, they do not act on the card's contents, and the design
	 * frame puts them outside it for that reason.
	 */
	footer: ReactNode
}

export const AddCollectorFrame = ({
	children,
	footer,
}: AddCollectorFrameProps) => {
	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div className="flex min-w-0 flex-col gap-1">
					<SectionTitle>Add remote collector</SectionTitle>
					<SectionDescription>
						On-prem collector · receives on-prem syslog and ships to AIR® ·
						Linux native install
					</SectionDescription>
				</div>

				<WizardStepRail steps={WIZARD_STEPS} activeStep={ACTIVE_STEP} />
			</div>

			<div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
				<Card>
					<CardHeader>
						<CardTitle>Install the collector</CardTitle>
						<CardDescription>
							{/* Says where the failures will be *before* anyone goes looking
								for them here: the installer runs on the customer's host, so
								AIR® never sees the error that stopped it. */}
							Run the one-liner on the collector host. Outbound HTTPS only — no
							inbound ports to open. Install errors appear in your terminal, not
							in AIR®.
						</CardDescription>
					</CardHeader>

					<CardContent className="flex flex-col gap-5">{children}</CardContent>
				</Card>

				<div className="flex items-center justify-end gap-3">{footer}</div>
			</div>
		</div>
	)
}

/**
 * What the host must be, as one line.
 *
 * A sentence rather than a checklist, because none of the three is something the
 * operator ticks off here — they are facts about the machine they are about to
 * run the command on, and a list of empty checkboxes would invite someone to
 * tick one.
 */
export const HostRequirements = () => {
	return (
		<div className="flex flex-col gap-1.5">
			<p className="text-sm font-medium">Requirements</p>

			<p className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-sm">
				{COLLECTOR_HOST_REQUIREMENTS.map(({ label, critical }, index) => (
					<span key={label} className="flex items-center gap-2">
						{index > 0 ? <span aria-hidden>·</span> : null}
						<span className={critical ? 'text-destructive-text' : undefined}>
							{label}
						</span>
					</span>
				))}
			</p>
		</div>
	)
}

type InstallCommandFrameProps = {
	/** The command itself — a `pre` when it is known, line boxes when it is not. */
	children: ReactNode
	/** The copy control, which only the resolved step can wire to a real string. */
	action: ReactNode
}

/**
 * The block the install one-liner is presented in: its label, its border, and
 * the shell chip that says what the command is written for.
 *
 * Shared with the skeleton, like everything else here — the chrome and the body
 * load at different times, and the block's height is what the card below it
 * depends on.
 */
export const InstallCommandFrame = ({
	children,
	action,
}: InstallCommandFrameProps) => {
	return (
		<div className="flex flex-col gap-1.5">
			<p className="text-sm font-medium">Run on the collector host</p>

			<div className="overflow-hidden rounded-lg border">
				<div className="bg-muted/40 flex items-center justify-between gap-2 border-b px-3 py-1.5">
					<span className="text-muted-foreground font-mono text-xs">
						{INSTALL_COMMAND_SHELL}
					</span>
					{action}
				</div>

				{children}
			</div>
		</div>
	)
}

type StaticIpAcknowledgementProps = {
	checked?: boolean
	onCheckedChange?: (checked: boolean) => void
	/** The skeleton's variant: the sentence is real, there is nothing to tick yet. */
	disabled?: boolean
}

/**
 * The one thing the operator has to agree to before leaving this screen.
 *
 * Owns its own label pairing rather than taking an id, so no caller can render
 * the sentence beside an unlabelled box — and the sentence itself lives here, in
 * the eager module, because the skeleton renders it for real (P-05 §1).
 *
 * The box is **nested inside** the label rather than paired with it by
 * `htmlFor`: Base UI puts a caller's `id` on the checkbox's hidden input, so an
 * `htmlFor` pointing at it labels an element that cannot be clicked — the
 * sentence renders as a label and does nothing when you click it.
 */
export const StaticIpAcknowledgement = ({
	checked,
	onCheckedChange,
	disabled,
}: StaticIpAcknowledgementProps) => {
	return (
		<Label
			className={cn(
				'flex items-start gap-3 text-sm font-normal',
				disabled && 'text-muted-foreground',
			)}
		>
			<Checkbox
				checked={checked}
				onCheckedChange={(value) => onCheckedChange?.(value === true)}
				disabled={disabled}
			/>
			<span>
				I understand this host must keep a static IP — changing it will break
				ingestion.
			</span>
		</Label>
	)
}
